import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation } from 'wouter';
import { 
  User, 
  ShoppingBag, 
  Heart, 
  ClipboardList, 
  BookOpen, 
  Building2, 
  HelpCircle, 
  LogOut, 
  ChevronRight, 
  Phone, 
  Sparkles, 
  Lock, 
  FileText,
  Mail,
  ShieldCheck,
  MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function Account() {
  const { user, userData, logout, signInWithGoogle, loading } = useAuth();
  const [, setLocation] = useLocation();

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      toast.success('Signed in successfully!');
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') return;
      console.error(err);
      toast.error('Sign-in failed. Please try again.');
    }
  };

  const menuSections = [
    {
      title: 'Shopping & Orders',
      items: [
        { label: 'My Orders', icon: ClipboardList, href: '/orders', desc: 'Track dispatches & history' },
        { label: 'Saved Wishlist', icon: Heart, href: '/wishlist', desc: 'Your favorite spices' },
        { label: 'Shopping Bag', icon: ShoppingBag, href: '/cart', desc: 'Review items & checkout' },
        { label: 'Browse Collection', icon: Sparkles, href: '/products', desc: 'All whole & ground spices' },
      ]
    },
    {
      title: 'Company & Catalog',
      items: [
        { label: 'Digital Lookbook', icon: BookOpen, href: '/brochure', desc: 'Interactive harvest catalog' },
        { label: 'Wholesale & B2B', icon: Building2, href: '/wholesale', desc: 'Bulk supply & export' },
        { label: 'About Ajjas Masala', icon: FileText, href: '/about', desc: 'Our legacy and farms' },
        { label: 'Recipes & Blog', icon: BookOpen, href: '/blog', desc: 'Culinary secrets & guides' },
      ]
    },
    {
      title: 'Support & Portals',
      items: [
        { label: 'Contact Customer Care', icon: HelpCircle, href: '/contact', desc: 'Direct phone & email support' },
        { label: 'Fulfillment Staff Portal', icon: MapPin, href: '/facility-login', desc: 'Center staff login' },
        { label: 'Admin Management', icon: Lock, href: '/admin', desc: 'Store & orders administration' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F4] text-[#1B120C] pb-28 pt-4 px-4 sm:px-6 max-w-xl mx-auto font-sans">
      {/* Header Profile Card */}
      <div className="bg-[#FAF5EB] border border-[rgba(27,18,12,0.1)] rounded-3xl p-6 shadow-sm mb-6">
        {user ? (
          <div className="flex items-center gap-4">
            <img 
              src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email || 'User')}&background=961A1A&color=fff`} 
              alt="Avatar"
              referrerPolicy="no-referrer"
              className="w-16 h-16 rounded-2xl object-cover border-2 border-[#961A1A]/30 shadow-md"
            />
            <div className="flex-1 min-w-0">
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#961A1A]">Logged In</span>
              <h2 className="text-xl font-bold truncate text-[#1B120C]">{user.displayName || 'Spice Connoisseur'}</h2>
              <p className="text-xs text-[#63574E] truncate font-medium">{user.email || user.phoneNumber}</p>
            </div>
            <button
              onClick={() => {
                logout();
                toast.success('Logged out successfully');
              }}
              aria-label="Logout"
              className="p-3 bg-red-50 hover:bg-red-100 text-[#961A1A] rounded-2xl transition-colors border border-red-200"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-[#961A1A]/10 text-[#961A1A] rounded-2xl flex items-center justify-center mx-auto mb-3 border border-[#961A1A]/20">
              <User className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold text-[#1B120C] mb-1">Welcome to Ajjas Masala</h2>
            <p className="text-xs text-[#63574E] mb-5 max-w-xs mx-auto">
              Sign in to track orders, save favorites, and enjoy instant checkout.
            </p>
            <div className="flex flex-col gap-2.5 max-w-xs mx-auto">
              <button
                onClick={handleGoogleSignIn}
                className="w-full bg-[#961A1A] text-white py-3.5 px-4 rounded-xl font-black uppercase tracking-widest text-xs shadow-md shadow-[#961A1A]/20 active:scale-95 transition-all"
              >
                Sign In with Google
              </button>
              <Link
                href="/login"
                className="w-full bg-white text-[#1B120C] border border-[rgba(27,18,12,0.15)] py-3 px-4 rounded-xl font-black uppercase tracking-widest text-[11px] text-center hover:bg-stone-50 active:scale-95 transition-all"
              >
                Email or Phone Login
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* WhatsApp Quick Help Action */}
      <a 
        href="https://wa.me/916360037355?text=Hello Ajjas Masala, I need help with an order." 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center justify-between p-4 bg-[#25D366]/10 border border-[#25D366]/30 rounded-2xl mb-6 hover:bg-[#25D366]/15 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#25D366] text-white rounded-xl flex items-center justify-center shadow-sm">
            <Phone className="w-5 h-5 fill-current" />
          </div>
          <div>
            <h4 className="text-xs font-black uppercase tracking-wider text-[#1B120C]">WhatsApp Quick Support</h4>
            <p className="text-[11px] text-[#63574E]">Direct chat with our spice experts</p>
          </div>
        </div>
        <span className="text-xs font-bold text-[#25D366] uppercase tracking-wider">Chat &rarr;</span>
      </a>

      {/* Menu Sections */}
      <div className="space-y-6">
        {menuSections.map((sec, idx) => (
          <div key={idx} className="bg-[#FAF5EB] border border-[rgba(27,18,12,0.1)] rounded-3xl p-4 shadow-sm">
            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#63574E] px-3 pt-1 pb-2">
              {sec.title}
            </h3>
            <div className="divide-y divide-[rgba(27,18,12,0.06)]">
              {sec.items.map((item, itemIdx) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={itemIdx}
                    href={item.href}
                    className="flex items-center justify-between p-3.5 hover:bg-white/60 rounded-xl transition-all active:scale-[0.99] group"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-white border border-[rgba(27,18,12,0.08)] flex items-center justify-center text-[#961A1A] group-hover:bg-[#961A1A] group-hover:text-white transition-colors">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-[#1B120C] group-hover:text-[#961A1A] transition-colors truncate">
                          {item.label}
                        </p>
                        <p className="text-[11px] text-[#63574E] truncate">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#63574E]/60 group-hover:text-[#1B120C] group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center text-xs text-[#63574E]/60 space-y-1">
        <p className="font-bold uppercase tracking-widest text-[10px]">Ajjas Masala &bull; Authentic Spices</p>
        <p className="text-[10px]">&copy; {new Date().getFullYear()} All rights reserved</p>
      </div>
    </div>
  );
}
